import { PDFScriptLoaderService, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

/**
 * ngx-extended-pdf-viewer requests its pdf.js bundles (viewer, worker, sandbox)
 * with a .mjs extension. nginx only maps .mjs to a JavaScript MIME type from
 * 1.25.1 onwards, so older deployments serve them as application/octet-stream and
 * the browser refuses to run them unless mime.types is patched per environment.
 *
 * The bundles are plain IIFEs with no top-level import/export, so an identical .js
 * copy loads the same way. patch-pdfjs-assets.js creates those copies at install
 * time and angular.json keeps the .mjs originals out of dist; this redirects every
 * bundle request the viewer makes to the .js name.
 *
 * Paths are rewritten rather than rebuilt so that the naming scheme (version,
 * .min and -es5 variants) stays owned by the library.
 */

/** Bundles that must keep type="module", matched after the extension rewrite. */
const PDFJS_BUNDLE = /\/(viewer|pdf\.worker|pdf\.sandbox)-[\d.]+(-es5|\.min)?\.js$/;

const toJsExtension = (assetPath: string): string => assetPath.replace(/\.mjs$/, '.js');

let patched = false;

export function usePlainJsPdfJsAssets(): void {
  if (patched) {
    return;
  }
  patched = true;

  const { workerSrc, sandboxBundleSrc } = pdfDefaultOptions;
  pdfDefaultOptions.workerSrc = () => toJsExtension(workerSrc());
  pdfDefaultOptions.sandboxBundleSrc = () => toJsExtension(sandboxBundleSrc());

  // The viewer path is built inside the loader service, whose methods are private
  // to TypeScript only, so they have to be wrapped on the prototype.
  const loader = PDFScriptLoaderService.prototype as any;

  const getPdfJsPath = loader.getPdfJsPath;
  loader.getPdfJsPath = function (artifact: string): string {
    return toJsExtension(getPdfJsPath.call(this, artifact));
  };

  // The loader picks the script type from the extension, so .js bundles would be
  // loaded as classic scripts. Keep them as modules to preserve the original
  // strict-mode and deferred-execution behaviour.
  const createScriptElement = loader.createScriptElement;
  loader.createScriptElement = function (sourcePath: string): HTMLScriptElement {
    const script = createScriptElement.call(this, sourcePath);
    if (PDFJS_BUNDLE.test(sourcePath)) {
      script.type = 'module';
    }
    return script;
  };
}
